import { merge, Observable, Subject } from 'rxjs';
import { filter, map, throttleTime } from 'rxjs/operators';

export interface IVideoProgressEvent {
    seconds: number;
    duration: number;
    percent: number;
}

export interface IVimeoPlayerEvent {
    type: string;
    data?: any;
}

export class FiVimeoPlayerApi {
    private readonly VIMEO_ORIGIN = 'https://player.vimeo.com';
    private messageHandler: ((event: MessageEvent) => void) | null = null;
    private progressSubject = new Subject<IVideoProgressEvent>();
    private eventSubject = new Subject<IVimeoPlayerEvent>();
    private playerId: string;
    private iframe: HTMLIFrameElement | null = null;
    private videoDuration: number = 0;
    private isPlaying: boolean = false;
    private isVideoFinished: boolean = false;
    private progressPollingInterval: any;

    /**
     * Initialize the service with a player ID and iframe element
     */
    public initialize(playerId: string, iframe: HTMLIFrameElement): void {
        this.playerId = playerId;
        this.iframe = iframe;
        this.setupMessageListener();
    }

    /**
     * Get progress events as Observable
     * Throttled to emit at most once per second to reduce frequency
     * (Vimeo sends playProgress/timeupdate events multiple times per second (often 4–10 times per second))
     */
    public getProgress$(): Observable<IVideoProgressEvent> {
        const progress$ = this.progressSubject.asObservable().pipe(throttleTime(1000)); // Emit at most once per second

        const finished$ = this.eventSubject.asObservable().pipe(
            filter((event) => event.type === 'finish'),
            map((): IVideoProgressEvent => {
                const duration = this.videoDuration;
                const seconds = duration > 0 ? duration : 0;
                return {
                    seconds,
                    duration: seconds,
                    percent: 100,
                };
            }),
        );

        return merge(progress$, finished$);
    }

    /**
     * Get all player events as Observable
     */
    public getEvents$(): Observable<IVimeoPlayerEvent> {
        return this.eventSubject.asObservable();
    }

    /**
     * Get current video duration
     */
    public getDuration(): number {
        return this.videoDuration;
    }

    /**
     * Check if video is currently playing
     */
    public getIsPlaying(): boolean {
        return this.isPlaying;
    }

    /**
     * Check if video is finished
     */
    public getIsFinished(): boolean {
        return this.isVideoFinished;
    }

    /**
     * Set up event listeners for the Vimeo player
     */
    public setupEventListeners(): void {
        if (!this.iframe?.contentWindow) {
            return;
        }

        const setupListeners = () => {
            const events = ['playProgress', 'timeupdate', 'play', 'pause', 'durationchange', 'finish'];

            events.forEach((eventName) => {
                this.sendMessage({
                    method: 'addEventListener',
                    value: eventName,
                    player_id: this.playerId,
                });
            });
        };

        if (this.iframe.contentWindow) {
            setupListeners();
        } else {
            this.iframe.addEventListener('load', setupListeners, { once: true });
        }
    }

    /**
     * Request video duration from player
     */
    public requestDuration(): void {
        this.sendMessage({
            method: 'getDuration',
            player_id: this.playerId,
        });
    }

    /**
     * Request current playback time from player
     */
    public requestCurrentTime(): void {
        this.sendMessage({
            method: 'getCurrentTime',
            player_id: this.playerId,
        });
    }

    /**
     * Start polling for progress updates (fallback mechanism)
     */
    public startProgressPolling(): void {
        this.stopProgressPolling();

        if (!this.isPlaying || this.isVideoFinished) {
            return;
        }

        this.progressPollingInterval = setInterval(() => {
            if (this.isPlaying && !this.isVideoFinished && this.iframe) {
                this.requestCurrentTime();
            } else {
                this.stopProgressPolling();
            }
        }, 1000);
    }

    /**
     * Stop polling for progress updates
     */
    public stopProgressPolling(): void {
        if (this.progressPollingInterval) {
            clearInterval(this.progressPollingInterval);
            this.progressPollingInterval = null;
        }
    }

    /**
     * Send a message to the Vimeo player iframe
     */
    private sendMessage(message: any): void {
        if (!this.iframe?.contentWindow) {
            return;
        }

        this.iframe.contentWindow.postMessage(JSON.stringify(message), this.VIMEO_ORIGIN);
    }

    /**
     * Set up window message listener for Vimeo events
     */
    private setupMessageListener(): void {
        if (this.messageHandler) {
            return;
        }

        this.messageHandler = (event: MessageEvent) => {
            if (event.origin !== this.VIMEO_ORIGIN) {
                return;
            }

            const data = this.parseMessageData(event.data);
            if (!data) {
                return;
            }

            this.handleMessage(data);
        };

        window.addEventListener('message', this.messageHandler);
    }

    /**
     * Parse message data (handle both string and object formats)
     */
    private parseMessageData(data: any): any {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch (e) {
                return null;
            }
        } else if (data && typeof data === 'object') {
            return data;
        }
        return null;
    }

    /**
     * Handle incoming messages from Vimeo player
     */
    private handleMessage(data: any): void {
        // Filter by player ID if specified
        if (data.player_id && data.player_id !== this.playerId) {
            return;
        }

        // Handle method responses
        if (data.method && data.value !== undefined) {
            this.handleMethodResponse(data.method, data.value);
            return;
        }

        // Handle events
        const eventType = data.event || data.type;
        if (eventType) {
            this.handleEvent(eventType, data.data);
        }
    }

    /**
     * Handle method responses from Vimeo
     */
    private handleMethodResponse(method: string, value: any): void {
        switch (method) {
            case 'getDuration':
                if (typeof value === 'number' && value > 0) {
                    this.videoDuration = value;
                }
                break;
            case 'getCurrentTime':
                if (typeof value === 'number' && value >= 0) {
                    if (this.isPlaying && !this.isVideoFinished) {
                        this.emitProgress(value, this.videoDuration);
                    }
                }
                break;
        }
    }

    /**
     * Handle events from Vimeo player
     */
    private handleEvent(eventType: string, eventData?: any): void {
        this.eventSubject.next({ type: eventType, data: eventData });

        switch (eventType) {
            case 'ready':
                this.isVideoFinished = false;
                this.requestDuration();
                break;
            case 'play':
                this.isPlaying = true;
                this.isVideoFinished = false;
                this.startProgressPolling();
                break;
            case 'pause':
                this.isPlaying = false;
                this.stopProgressPolling();
                break;
            case 'playProgress':
                if (this.isPlaying && !this.isVideoFinished && eventData && typeof eventData.seconds === 'number') {
                    if (typeof eventData.duration === 'number' && eventData.duration > 0) {
                        this.videoDuration = eventData.duration;
                    }
                    this.emitProgress(eventData.seconds, this.videoDuration);
                }
                break;
            case 'timeupdate':
                if (this.isPlaying && !this.isVideoFinished && eventData && typeof eventData.seconds === 'number') {
                    if (typeof eventData.duration === 'number' && eventData.duration > 0) {
                        this.videoDuration = eventData.duration;
                    }
                    this.emitProgress(eventData.seconds, this.videoDuration);
                }
                break;
            case 'loaded':
                this.requestDuration();
                break;
            case 'durationchange':
                if (eventData && typeof eventData.duration === 'number' && eventData.duration > 0) {
                    this.videoDuration = eventData.duration;
                }
                break;
            case 'finish':
                this.isVideoFinished = true;
                this.isPlaying = false;
                this.stopProgressPolling();
                break;
        }
    }

    /**
     * Emit progress event
     */
    private emitProgress(seconds: number, duration?: number): void {
        if (typeof seconds !== 'number' || seconds < 0) {
            return;
        }

        const finalDuration = duration ?? this.videoDuration;

        if (finalDuration && finalDuration > 0) {
            const percent = (seconds / finalDuration) * 100;
            this.progressSubject.next({
                seconds,
                duration: finalDuration,
                percent: Math.min(100, Math.max(0, percent)),
            });
        } else {
            this.progressSubject.next({
                seconds,
                duration: 0,
                percent: 0,
            });
        }
    }

    /**
     * Clean up resources - call this when component is destroyed
     */
    public destroy(): void {
        this.stopProgressPolling();
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
            this.messageHandler = null;
        }
        this.progressSubject.complete();
        this.eventSubject.complete();
    }
}
